// MediaCenter.js
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Seo from "../../components/Seo/Seo";
import { mediaPosts } from "./mediaData";
import "./MediaCenter.css";

export default function MediaCenter() {
  const { t } = useTranslation("common");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("latest");
  const [activeArticle, setActiveArticle] = useState(null);
  
  const postsPerPage = 9;

  // Filter and sort posts
  const filteredAndSortedPosts = useMemo(() => {
    const filtered = mediaPosts.filter((post) => {
      const title = t(post.titleKey || "");
      const excerpt = t(post.excerptKey || "");

      return (
        title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "latest":
          return new Date(b.date) - new Date(a.date);
        case "oldest":
          return new Date(a.date) - new Date(b.date);
        case "title":
          return t(a.titleKey).localeCompare(t(b.titleKey), "ar");
        default:
          return 0;
      }
    });

    return sorted;
  }, [searchQuery, sortBy, t]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedPosts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredAndSortedPosts.slice(indexOfFirstPost, indexOfLastPost);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <section className="page">
      <Seo
        title={t("media.seo.title")}
        description={t("media.seo.description")}
        path="/media-center"
      />

      <div className="container">
        <div className="pageHead">
          <h1>{t("media.pageHead.title")}</h1>
          <p className="muted">
            {t("media.pageHead.subtitle")}
          </p>
        </div>

        {/* Filters Section */}
        <div className="mediaFilters">
          <div className="searchBox">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder={t("media.filters.search.placeholder")}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchQuery && (
              <button 
                className="clearSearch"
                onClick={() => setSearchQuery("")}
                aria-label={t("media.filters.search.clear")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>

          <div className="sortBox">
            <label htmlFor="sort">{t("media.filters.sort.label")}</label>
            <select 
              id="sort"
              value={sortBy} 
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="latest">{t("media.filters.sort.latest")}</option>
              <option value="oldest">{t("media.filters.sort.oldest")}</option>
              <option value="title">{t("media.filters.sort.title")}</option>
            </select>
          </div>
        </div>

        {/* Results Info */}
        <div className="resultsInfo">
          {searchQuery ? (
            <p>
              {t("media.results.search", {
                count: filteredAndSortedPosts.length,
                result: filteredAndSortedPosts.length === 1 ? t("media.results.single") : t("media.results.plural"),
                query: searchQuery
              })}
            </p>
          ) : (
            <p>{t("media.results.total", { count: filteredAndSortedPosts.length })}</p>
          )}
        </div>

        {/* News Grid */}
        {currentPosts.length > 0 ? (
          <div className="mediaGrid">
            {currentPosts.map((post) => (
              <div key={post.id} className="mediaCard">
                {post.image && (
                  <div className="mediaCard__image">
                    <img src={post.image} alt={t(post.titleKey)} />
                  </div>
                )}

                <div className="mediaCard__content">
                  <div className="mediaCard__date">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    {formatDate(post.date)}
                  </div>

                  <h3 className="mediaCard__title">
                    {t(post.titleKey)}
                  </h3>

                  <p className="mediaCard__excerpt">
                    {t(post.excerptKey)}
                  </p>

                  <button 
                    className="mediaCard__readMore"
                    onClick={() => setActiveArticle(post)}
                  >
                    {t("media.card.readMore")}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="noResults">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <h3>{t("media.noResults.title")}</h3>
            <p>{t("media.noResults.message")}</p>
            <button onClick={() => setSearchQuery("")} className="resetSearch">
              {t("media.noResults.reset")}
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && currentPosts.length > 0 && (
          <div className="pagination">
            <button
              className="paginationBtn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label={t("media.pagination.previous")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>

            <div className="paginationNumbers">
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      className={`paginationNumber ${currentPage === pageNumber ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return <span key={pageNumber} className="paginationDots">...</span>;
                }
                return null;
              })}
            </div>

            <button
              className="paginationBtn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label={t("media.pagination.next")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Article Details Modal */}
      {activeArticle && (
        <div className="mediaModal">
          <div
            className="mediaModal__overlay"
            onClick={() => setActiveArticle(null)}
          />

          <div className="mediaModal__content">
            <button
              className="mediaModal__close"
              onClick={() => setActiveArticle(null)}
              aria-label={t("media.modal.close")}
            >
              ✕
            </button>

            {activeArticle.image && (
              <div className="mediaModal__image">
                <img src={activeArticle.image} alt={t(activeArticle.titleKey)} />
              </div>
            )}

            <h2 className="mediaModal__title">{t(activeArticle.titleKey)}</h2>
            
            <div className="mediaModal__date">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {formatDate(activeArticle.date)}
            </div>

            <div className="mediaModal__description">
              {t(activeArticle.contentKey)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}